import { useCallback, useRef } from 'react'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'

import { useLenis } from '@/contexts/lenis'
import { useMotionMatchMedia, useMotionTouchMedia } from '@/hooks'
import { useLoaderStore } from '@/store/loader'

import {
  createLinesMotion,
  createMaskMotion,
  createWordsMotion,
  getScrollTriggerOptions,
  Motion
} from './shared'

export const useHomeDrMotion = () => {
  const motionRef = useRef<HTMLDivElement>(null)
  const introScrollTrigger = useRef<ScrollTrigger>(null!)
  const profileScrollTrigger = useRef<ScrollTrigger>(null!)
  const textScrollTrigger = useRef<ScrollTrigger>(null!)
  const parallaxScrollTrigger = useRef<ScrollTrigger>(null!)
  const fontsReady = useLoaderStore((state) => state.fontsReady)
  const { lenis } = useLenis()

  const introTitleMotion = useRef<Motion | null>(null)
  const introParagraphMotion = useRef<Motion | null>(null)
  const profileTitleMotion = useRef<Motion | null>(null)
  const textTitleMotion = useRef<Motion | null>(null)
  const textParagraphMotion = useRef<Motion | null>(null)

  const revertIntroSplitText = useCallback(() => {
    introTitleMotion.current?.revert()
    introParagraphMotion.current?.revert()
  }, [])

  const revertProfileSplitText = useCallback(() => {
    profileTitleMotion.current?.revert()
  }, [])

  const revertTextSplitText = useCallback(() => {
    textTitleMotion.current?.revert()
    textParagraphMotion.current?.revert()
  }, [])

  /**
   * reveal setup
   */
  useMotionTouchMedia(
    {
      pointer({ selector }) {
        if (!fontsReady || !lenis) return

        const rows = selector?.('[data-motion-row]')
        const title = selector?.('[data-motion-intro-title]')[0]
        const paragraph = selector?.('[data-motion-intro-paragraph]')[0]

        introTitleMotion.current = title ? createWordsMotion(title) : null
        introParagraphMotion.current = paragraph ? createLinesMotion(paragraph) : null

        const animation = gsap.timeline({ onComplete: revertIntroSplitText })
        introTitleMotion.current && animation.add(introTitleMotion.current.timeline, 0)
        introParagraphMotion.current && animation.add(introParagraphMotion.current.timeline, 0.1)

        introScrollTrigger.current = ScrollTrigger.create({
          ...getScrollTriggerOptions({
            trigger: rows[0],
            animation
          })
        })
      },

      revert() {
        introScrollTrigger.current?.kill()
        revertIntroSplitText()
      }
    },
    { scope: motionRef, dependencies: [fontsReady, lenis] }
  )

  useMotionTouchMedia(
    {
      pointer({ selector }) {
        if (!fontsReady || !lenis) return

        const rows = selector?.('[data-motion-row]')
        const title = selector?.('[data-motion-profile-title]')[0]
        profileTitleMotion.current = title ? createMaskMotion(title) : null

        const animation = gsap.timeline({ onComplete: revertProfileSplitText })
        profileTitleMotion.current && animation.add(profileTitleMotion.current.timeline, 0)

        profileScrollTrigger.current = ScrollTrigger.create({
          ...getScrollTriggerOptions({
            trigger: rows[1],
            animation
          })
        })
      },

      revert() {
        profileScrollTrigger.current?.kill()
        revertProfileSplitText()
      }
    },
    { scope: motionRef, dependencies: [fontsReady, lenis] }
  )

  useMotionTouchMedia(
    {
      pointer({ selector }) {
        if (!fontsReady || !lenis) return

        const rows = selector?.('[data-motion-row]')
        const title = selector?.('[data-motion-text-title]')[0]
        const paragraph = selector?.('[data-motion-text-paragraph]')[0]
        const button = selector?.('[data-motion-text-button]')[0]

        textTitleMotion.current = title ? createLinesMotion(title) : null
        textParagraphMotion.current = paragraph ? createLinesMotion(paragraph) : null

        const animation = gsap.timeline({ onComplete: revertTextSplitText })
        textTitleMotion.current && animation.add(textTitleMotion.current.timeline, 0)
        textParagraphMotion.current && animation.add(textParagraphMotion.current.timeline, 0.1)
        button &&
          animation.from(button, { opacity: 0, y: 20, duration: 0.6, ease: 'power2.out' }, 0.3)

        textScrollTrigger.current = ScrollTrigger.create({
          ...getScrollTriggerOptions({
            trigger: rows[2],
            animation
          })
        })
      },

      revert() {
        textScrollTrigger.current?.kill()
        revertTextSplitText()
      }
    },
    { scope: motionRef, dependencies: [fontsReady, lenis] }
  )

  /**
   * parallax setup
   */
  useMotionMatchMedia(
    {
      medium() {
        if (!lenis) return

        parallaxScrollTrigger.current = ScrollTrigger.create({
          trigger: motionRef.current,
          start: 'top bottom',
          end: 'bottom top',
          markers: false,
          scrub: true,
          animation: gsap.timeline({}).fromTo(
            '[data-motion-row]',
            { yPercent: 0 },
            {
              yPercent: (index) => (index === 0 ? 50 : index === 1 ? -50 : index === 2 ? -30 : 0),
              duration: 1,
              ease: 'none'
            }
          )
        })
      },

      revert() {
        parallaxScrollTrigger.current?.kill()
      }
    },
    { scope: motionRef, dependencies: [lenis] }
  )

  return { motionRef }
}
